import {Color} from '@benchling/nucleus-ui';

import {AntibodyProps} from 'antibodies/visualization/Antibody';
import {AntibodyDomainType} from 'graphql-client/__generated/graphqlGlobalTypes';

export const ANTIBODY_DOMAIN_CORNER_RADIUS = 4;
export const ANTIBODY_DOMAIN_HEIGHT = 64;
export const ANTIBODY_DOMAIN_STROKE_THICKNESS = 3;
export const ANTIBODY_DOMAIN_WIDTH = 32;

// TODO: Map by named specificity instead of indexing into the alphabet
// TODO: Fill in eight colors per domain type
export const COLORS_BY_DOMAIN_TYPE_AND_SPECIFICITY: Record<AntibodyDomainType, Array<string>> = {
  [AntibodyDomainType.VL]: [Color.LIME_400, Color.ORANGE_400],
  [AntibodyDomainType.CL]: [Color.LIME_200, Color.ORANGE_200],
  [AntibodyDomainType.VH]: [Color.BLUE_350, Color.PURPLE_400],
  [AntibodyDomainType.VHH]: [Color.BLUE_400],
  [AntibodyDomainType.CH1]: [Color.BLUE_200, Color.PURPLE_200],
  [AntibodyDomainType.CH2]: [Color.BLUE_200, Color.PURPLE_200],
  [AntibodyDomainType.CH3]: [Color.BLUE_200, Color.PURPLE_200],
  [AntibodyDomainType.CH4]: [Color.BLUE_200, Color.PURPLE_200],
  [AntibodyDomainType.J]: [Color.BLACK, Color.BLACK],
  [AntibodyDomainType.H]: [Color.BLACK, Color.BLACK],
  [AntibodyDomainType.L]: [Color.BLACK, Color.BLACK],
  [AntibodyDomainType.VA]: [Color.BLUE_350],
  [AntibodyDomainType.CA]: [Color.BLUE_200],
  [AntibodyDomainType.VB]: [Color.LIME_400],
  [AntibodyDomainType.CB]: [Color.LIME_200],
  [AntibodyDomainType.VG]: [Color.ORANGE_400],
  [AntibodyDomainType.CG]: [Color.ORANGE_200],
  [AntibodyDomainType.VD]: [Color.PURPLE_400],
  [AntibodyDomainType.CD]: [Color.PURPLE_200],
};

export const HOVER_COLORS_BY_DOMAIN_TYPE_AND_SPECIFICITY: Record<AntibodyDomainType, Array<string>> = {
  [AntibodyDomainType.VL]: [Color.LIME_450, Color.ORANGE_450],
  [AntibodyDomainType.CL]: [Color.LIME_250, Color.ORANGE_250],
  [AntibodyDomainType.VH]: [Color.BLUE_400, Color.PURPLE_450],
  [AntibodyDomainType.VHH]: [Color.BLUE_450],
  [AntibodyDomainType.CH1]: [Color.BLUE_250, Color.PURPLE_250],
  [AntibodyDomainType.CH2]: [Color.BLUE_250, Color.PURPLE_250],
  [AntibodyDomainType.CH3]: [Color.BLUE_250, Color.PURPLE_250],
  [AntibodyDomainType.CH4]: [Color.BLUE_250, Color.PURPLE_250],
  [AntibodyDomainType.J]: [Color.GRAY_600, Color.GRAY_600],
  [AntibodyDomainType.H]: [Color.GRAY_600, Color.GRAY_600],
  [AntibodyDomainType.L]: [Color.GRAY_600, Color.GRAY_600],
  [AntibodyDomainType.VA]: [Color.BLUE_400],
  [AntibodyDomainType.CA]: [Color.BLUE_250],
  [AntibodyDomainType.VB]: [Color.LIME_450],
  [AntibodyDomainType.CB]: [Color.LIME_250],
  [AntibodyDomainType.VG]: [Color.ORANGE_450],
  [AntibodyDomainType.CG]: [Color.ORANGE_250],
  [AntibodyDomainType.VD]: [Color.PURPLE_450],
  [AntibodyDomainType.CD]: [Color.PURPLE_250],
};

export const SELECTED_STROKE_COLORS_BY_DOMAIN_TYPE_AND_SPECIFICITY: Record<
  AntibodyDomainType,
  Array<string>
> = {
  [AntibodyDomainType.VL]: [Color.LIME_500, Color.ORANGE_500],
  [AntibodyDomainType.CL]: [Color.LIME_500, Color.ORANGE_500],
  [AntibodyDomainType.VH]: [Color.BLUE_500, Color.PURPLE_500],
  [AntibodyDomainType.VHH]: [Color.BLUE_500],
  [AntibodyDomainType.CH1]: [Color.BLUE_500, Color.PURPLE_500],
  [AntibodyDomainType.CH2]: [Color.BLUE_500, Color.PURPLE_500],
  [AntibodyDomainType.CH3]: [Color.BLUE_500, Color.PURPLE_500],
  [AntibodyDomainType.CH4]: [Color.BLUE_500, Color.PURPLE_500],
  [AntibodyDomainType.J]: [Color.GRAY_700, Color.GRAY_700],
  [AntibodyDomainType.H]: [Color.GRAY_700, Color.GRAY_700],
  [AntibodyDomainType.L]: [Color.GRAY_700, Color.GRAY_700],
  [AntibodyDomainType.VA]: [Color.BLUE_500],
  [AntibodyDomainType.CA]: [Color.BLUE_500],
  [AntibodyDomainType.VB]: [Color.LIME_500],
  [AntibodyDomainType.CB]: [Color.LIME_500],
  [AntibodyDomainType.VG]: [Color.ORANGE_500],
  [AntibodyDomainType.CG]: [Color.ORANGE_500],
  [AntibodyDomainType.VD]: [Color.PURPLE_500],
  [AntibodyDomainType.CD]: [Color.PURPLE_500],
};

export const FRIENDLY_NAMES_BY_DOMAIN_TYPE: Record<AntibodyDomainType, string> = {
  [AntibodyDomainType.VL]: 'Variable light',
  [AntibodyDomainType.CL]: 'Constant light',
  [AntibodyDomainType.VH]: 'Variable heavy',
  [AntibodyDomainType.VHH]: 'Single-domain variable heavy',
  [AntibodyDomainType.CH1]: 'Constant heavy 1',
  [AntibodyDomainType.CH2]: 'Constant heavy 2',
  [AntibodyDomainType.CH3]: 'Constant heavy 3',
  [AntibodyDomainType.CH4]: 'Constant heavy 4',
  [AntibodyDomainType.J]: 'Joiner',
  [AntibodyDomainType.H]: 'Hinge',
  [AntibodyDomainType.L]: 'Linker',
  [AntibodyDomainType.VA]: 'Variable alpha',
  [AntibodyDomainType.CA]: 'Constant alpha',
  [AntibodyDomainType.VB]: 'Variable beta',
  [AntibodyDomainType.CB]: 'Constant beta',
  [AntibodyDomainType.VG]: 'Variable gamma',
  [AntibodyDomainType.CG]: 'Constant gamma',
  [AntibodyDomainType.VD]: 'Variable delta',
  [AntibodyDomainType.CD]: 'Constant delta',
};

// TODO: what are the technical terms here?
export enum TerminusStyle {
  FLAT = 'FLAT',
  NOTCH = 'NOTCH',
}

// Copy/pasted out of Figma SVG. TODO: Calculate these from chains / interactions... or persist them
type AntibodyRestProps = Omit<AntibodyProps, 'format' | 'showLabels'>;
export const IGG_REST_PROPS: AntibodyRestProps = {
  displayByRef: {
    1: {
      type: AntibodyDomainType.VH,
      translate: {x: 10.28, y: 5.55},
      rotate: -35,
    },
    2: {
      type: AntibodyDomainType.CH1,
      translate: {x: 21.28, y: 20.96},
      rotate: -35,
    },
    3: {
      type: AntibodyDomainType.H,
      angle: 145,
      freeEndLength: 4,
      fixedEndLength: 8,
      translate: {x: 35.68, y: 33.03},
    },
    4: {
      type: AntibodyDomainType.CH2,
      translate: {x: 32.74, y: 42.57},
    },
    5: {
      type: AntibodyDomainType.CH3,
      translate: {x: 32.74, y: 61.65},
    },
    6: {
      type: AntibodyDomainType.VL,
      translate: {x: 0, y: 12.89},
      rotate: -35,
    },
    7: {
      type: AntibodyDomainType.CL,
      translate: {x: 11.01, y: 28.3},
      rotate: -35,
    },
    8: {
      type: AntibodyDomainType.VH,
      translate: {x: 69.69, y: 0},
      rotate: 35,
    },
    9: {
      type: AntibodyDomainType.CH1,
      translate: {x: 58.96, y: 15.49},
      rotate: 35,
    },
    10: {
      type: AntibodyDomainType.H,
      angle: 145,
      freeEndLength: 4,
      fixedEndLength: 8,
      translate: {x: 52.56, y: 33.03},
      reflect: true,
    },
    11: {
      type: AntibodyDomainType.CH2,
      translate: {x: 45.96, y: 42.57},
    },
    12: {
      type: AntibodyDomainType.CH3,
      translate: {x: 45.96, y: 61.65},
    },
    13: {
      type: AntibodyDomainType.VL,
      translate: {x: 80.7, y: 7.34},
      rotate: 35,
    },
    14: {
      type: AntibodyDomainType.CL,
      translate: {x: 69.97, y: 22.83},
      rotate: 35,
    },
  },
  sizing: {
    chainDomainSizing: {
      cornerRadius: 1.80392,
      height: 18.3486,
      width: 9.54128,
    },
    connectorSizing: {
      strokeThickness: 2,
    },
    svgSizing: {
      svgViewboxHeight: 82,
      svgViewboxWidth: 88,
    },
  },
};

export const NANOBODY_REST_PROPS: AntibodyRestProps = {
  displayByRef: {
    1: {
      type: AntibodyDomainType.VHH,
      translate: {x: 23.57, y: 0},
      rotate: 30,
    },
  },
  sizing: {
    chainDomainSizing: {
      cornerRadius: 4,
      height: 49.49,
      width: 22.57,
    },
    connectorSizing: {},
    svgSizing: {
      svgViewboxHeight: 55,
      svgViewboxWidth: 42,
    },
  },
};

export const SCFV_REST_PROPS: AntibodyRestProps = {
  displayByRef: {
    1: {
      type: AntibodyDomainType.VH,
      rotate: 30,
      translate: {x: 23.94, y: 2.04},
      nTerminusStyle: TerminusStyle.NOTCH,
    },
    2: {
      type: AntibodyDomainType.L,
      rotate: 30,
      translate: {x: 38.16, y: 0.87},
      centerLength: 49.49,
      endRadius: 8.57,
      tipLength: 8,
    },
    3: {
      type: AntibodyDomainType.VL,
      rotate: 30,
      translate: {x: 53.91, y: 19.56},
      nTerminusStyle: TerminusStyle.NOTCH,
    },
  },
  sizing: {
    chainDomainSizing: {
      cornerRadius: 4,
      height: 49.49,
      width: 22.57,
    },
    connectorSizing: {
      strokeThickness: 2,
    },
    svgSizing: {
      svgViewboxHeight: 73,
      svgViewboxWidth: 73,
    },
  },
};

export const FAB_REST_PROPS: AntibodyRestProps = {
  displayByRef: {
    1: {
      type: AntibodyDomainType.VH,
      rotate: 30,
      translate: {x: 33.95, y: 0},
    },
    2: {
      type: AntibodyDomainType.CH1,
      rotate: 30,
      translate: {x: 16.62, y: 30.27},
    },
    3: {
      type: AntibodyDomainType.CL,
      rotate: 30,
      translate: {x: 54.08, y: 10.77},
    },
    4: {
      type: AntibodyDomainType.CL,
      rotate: 30,
      translate: {x: 36.62, y: 41.27},
    },
  },
  sizing: {
    chainDomainSizing: {
      cornerRadius: 2.68,
      height: 33.24,
      width: 15.16,
    },
    connectorSizing: {},
    svgSizing: {
      svgViewboxHeight: 80,
      svgViewboxWidth: 67,
    },
  },
};

export const TCR_REST_PROPS: AntibodyRestProps = {
  displayByRef: {
    1: {
      type: AntibodyDomainType.VA,
      nTerminusStyle: TerminusStyle.NOTCH,
      translate: {x: 0, y: 1},
    },
    2: {
      type: AntibodyDomainType.CA,
      translate: {x: 0, y: 20.08},
    },
    3: {
      type: AntibodyDomainType.VB,
      nTerminusStyle: TerminusStyle.NOTCH,
      translate: {x: 13.21, y: 1},
    },
    4: {
      type: AntibodyDomainType.CB,
      translate: {x: 13.21, y: 20.08},
    },
  },
  sizing: {
    chainDomainSizing: {
      cornerRadius: 1.8,
      height: 18.35,
      width: 9.54,
    },
    connectorSizing: {},
    svgSizing: {
      svgViewboxHeight: 39,
      svgViewboxWidth: 23,
    },
  },
};

export const ANTIBODY_REST_PROPS_BY_ID: Record<string, AntibodyRestProps> = {
  bnchfmt_IgGmono: IGG_REST_PROPS,
  bnchfmt_IgGbi: IGG_REST_PROPS,
  bnchfmt_nano: NANOBODY_REST_PROPS,
  bnchfmt_scFv: SCFV_REST_PROPS,
  bnchfmt_Fab: FAB_REST_PROPS,
  bnchfmt_TCR: TCR_REST_PROPS,
};
